import {
    useExpensesSummaryPageLazyQuery,
    useExpensesSummarySuppliersQuery,
} from '../../../../services';
import {
    DBlock,
    DBlockHeader,
    DCheckbox,
    DMonth,
    DPage,
    DPageBody,
    DYear,
    useDMonth,
    useDYear,
} from '../../../dum';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { ExpensesSummaryComparisonTable } from './expenses-summary-comparison-table';
import {
    ExpensesSummaryKpiData,
    ExpensesSummaryKpis,
} from './expenses-summary-kpis';
import { ExpensesSummaryTrendChart } from './expenses-summary-trend-chart';
import { ExpensesSummaryMissingPanel } from './expenses-summary-missing-panel';
import { SGenerateRecurringExpensesDialog } from '../../../smart';
import {
    atEntityLevel,
    atResourceLevel,
    bucketByEntity,
    buildComparisonRows,
    monthLabelOf,
    NormalizedRow,
    shiftMonth,
    sumBucket,
} from './expenses-summary-model';

export function ExpensesSummary() {
    const { year, setYear } = useDYear({
        COMPONENT_KEY: ExpensesSummary.name + '_Year',
        defaultValue: dayjs().year(),
    });

    // DMonth is 0-indexed; the backend is 1-indexed. Everything below converts
    // once, at selectedMonth.
    const { month, setMonth } = useDMonth({
        COMPONENT_KEY: ExpensesSummary.name + '_Month',
        defaultValue: dayjs().month(),
    });

    // Both default to false: reconciliation-only expenses stay out of the
    // totals and canceled expenses stay out of the totals.
    const [includeReconciliationOnly, setIncludeReconciliationOnly] =
        useState(false);
    const [includeCanceled, setIncludeCanceled] = useState(false);

    // 0 = por categoría, 1 = por proveedor.
    const [grouping, setGrouping] = useState(0);

    const [generateOpen, setGenerateOpen] = useState(false);

    const [query, { data, loading }] = useExpensesSummaryPageLazyQuery();

    // The recurring flag isn't part of any summary aggregate, so it comes from
    // the supplier list. Independent of year/month/toggles — fetched once.
    const { data: suppliersData } = useExpensesSummarySuppliersQuery();

    const recurringAccountIds = useMemo(() => {
        const ids = new Set<number>();
        (suppliersData?.getAccounts ?? []).forEach((account) => {
            if (account.supplier_recurring_expenses) ids.add(account.id);
        });
        return ids;
    }, [suppliersData]);

    // Only the year (and the two flags) drive the fetch — both years arrive at
    // once, so changing the month is a pure client-side recompute.
    useEffect(() => {
        if (year === null) return;
        void query({
            variables: {
                year,
                previousYear: year - 1,
                includeReconciliationOnly,
                includeCanceled,
            },
        });
    }, [query, year, includeReconciliationOnly, includeCanceled]);

    const { accountRows, categoryRows } = useMemo(() => {
        const accounts: NormalizedRow[] = [];
        const categories: NormalizedRow[] = [];

        [
            ...(data?.currentYearByAccount.expenses ?? []),
            ...(data?.previousYearByAccount.expenses ?? []),
        ].forEach((row) => {
            if (row.month == null) return;
            accounts.push({
                year: row.year,
                month: row.month,
                total: row.total,
                entityId: row.account_id ?? 0,
                entityName: row.account_name ?? 'Sin proveedor',
                resourceId: row.resource_id ?? 0,
                resourceName: row.resource_name ?? 'Sin recurso',
            });
        });

        [
            ...(data?.currentYearByCategory.expenses ?? []),
            ...(data?.previousYearByCategory.expenses ?? []),
        ].forEach((row) => {
            if (row.month == null) return;
            categories.push({
                year: row.year,
                month: row.month,
                total: row.total,
                entityId: row.resource_category_id ?? 0,
                entityName: row.resource_category_name ?? 'Sin categoría',
                resourceId: row.resource_id ?? 0,
                resourceName: row.resource_name ?? 'Sin recurso',
            });
        });

        return { accountRows: accounts, categoryRows: categories };
    }, [data]);

    // The rows behind whichever tab is active, plus the same rows collapsed to
    // the parent level (categories or suppliers).
    const activeRows = grouping === 0 ? categoryRows : accountRows;
    const activePoints = useMemo(
        () => atEntityLevel(activeRows),
        [activeRows],
    );
    const categoryPoints = useMemo(
        () => atEntityLevel(categoryRows),
        [categoryRows],
    );

    const selected = useMemo(() => {
        if (year === null || month === null) return null;
        return { year, month: month + 1 };
    }, [year, month]);

    const kpis: ExpensesSummaryKpiData = useMemo(() => {
        const empty = {
            monthTotal: 0,
            previousMonthTotal: 0,
            delta: 0,
            deltaPct: 0 as number | null,
            sameMonthLastYearTotal: 0,
            yearToDateTotal: 0,
        };
        if (!selected) return empty;

        // Either grouping sums to the same grand total — they partition the
        // same underlying expense-resource rows — so the category axis is used
        // as the canonical source for the headline figures.
        const monthTotal = sumBucket(
            bucketByEntity(categoryPoints, selected.year, selected.month),
        );
        const previousRef = shiftMonth(selected.year, selected.month, -1);
        const previousMonthTotal = sumBucket(
            bucketByEntity(categoryPoints, previousRef.year, previousRef.month),
        );
        const sameMonthLastYearTotal = sumBucket(
            bucketByEntity(categoryPoints, selected.year - 1, selected.month),
        );
        const yearToDateTotal = categoryPoints
            .filter(
                (point) =>
                    point.year === selected.year &&
                    point.month <= selected.month,
            )
            .reduce((acc, point) => acc + point.total, 0);

        const delta = monthTotal - previousMonthTotal;
        return {
            monthTotal,
            previousMonthTotal,
            delta,
            deltaPct: previousMonthTotal
                ? (delta / previousMonthTotal) * 100
                : monthTotal
                ? null
                : 0,
            sameMonthLastYearTotal,
            yearToDateTotal,
        };
    }, [categoryPoints, selected]);

    const comparisonRows = useMemo(() => {
        if (!selected) return [];
        return buildComparisonRows(
            activePoints,
            selected.year,
            selected.month,
        );
    }, [activePoints, selected]);

    // Drill-down: the same comparison, recomputed over the resources belonging
    // to one category (or bought from one supplier). Runs only when a row is
    // actually expanded, so the cost scales with what the user opens.
    const childRowsOf = useCallback(
        (row: { id: number }) => {
            if (!selected) return [];
            return buildComparisonRows(
                atResourceLevel(
                    activeRows.filter(
                        (activeRow) => activeRow.entityId === row.id,
                    ),
                ),
                selected.year,
                selected.month,
            );
        },
        [activeRows, selected],
    );

    const labels = useMemo(() => {
        if (!selected) return { month: '', previousMonth: '' };
        const previousRef = shiftMonth(selected.year, selected.month, -1);
        return {
            month: monthLabelOf(selected.year, selected.month),
            previousMonth: monthLabelOf(previousRef.year, previousRef.month),
        };
    }, [selected]);

    return (
        <DPage>
            <DPageBody disableGutters>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                        // Single scroll container; children keep their natural
                        // height instead of shrinking to fit (same pattern as
                        // Resumen de ventas).
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        '& > *': { flexShrink: 0, flexGrow: 0 },
                    }}
                >
                    <DBlock>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 2,
                            }}
                        >
                            <DYear setYear={setYear} year={year} />
                            <DMonth setMonth={setMonth} month={month} />
                            {/* DCheckbox stretches to 100% of its parent, so
                                each one gets a sized wrapper to keep the filter
                                row from collapsing. */}
                            <Box sx={{ width: 190 }}>
                                <DCheckbox
                                    value={includeReconciliationOnly}
                                    label={'Incluir solo conciliación'}
                                    onChange={setIncludeReconciliationOnly}
                                    disableGutters
                                />
                            </Box>
                            <Box sx={{ width: 150 }}>
                                <DCheckbox
                                    value={includeCanceled}
                                    label={'Canceladas'}
                                    onChange={setIncludeCanceled}
                                    disableGutters
                                />
                            </Box>
                        </Box>
                    </DBlock>

                    {!selected ? (
                        <DBlock>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 8,
                                }}
                            >
                                <Typography
                                    variant={'h6'}
                                    sx={{ color: 'text.secondary' }}
                                >
                                    Selecciona un año y un mes para ver la
                                    información.
                                </Typography>
                            </Box>
                        </DBlock>
                    ) : (
                        <>
                            <DBlock>
                                <DBlockHeader subtitle={'Resumen del mes'} />
                                <ExpensesSummaryKpis
                                    kpis={kpis}
                                    loading={loading}
                                    monthLabel={labels.month}
                                    previousMonthLabel={labels.previousMonth}
                                    year={selected.year}
                                />
                            </DBlock>

                            <DBlock>
                                <DBlockHeader subtitle={'¿Qué cambió?'} />
                                <Tabs
                                    value={grouping}
                                    onChange={(_, value) => setGrouping(value)}
                                    sx={{ mb: 2 }}
                                >
                                    <Tab label={'Por categoría'} />
                                    <Tab label={'Por proveedor'} />
                                </Tabs>
                                <Typography
                                    variant={'caption'}
                                    sx={{
                                        color: 'text.secondary',
                                        display: 'block',
                                        mb: 1,
                                    }}
                                >
                                    Ordenado por el mayor cambio. Haz clic en un
                                    encabezado para ordenar por esa columna, o
                                    expande una fila para ver el desglose por
                                    recurso.
                                </Typography>
                                <ExpensesSummaryComparisonTable
                                    rows={comparisonRows}
                                    loading={loading}
                                    entityColumnTitle={
                                        grouping === 0
                                            ? 'Categoría'
                                            : 'Proveedor'
                                    }
                                    previousMonthLabel={labels.previousMonth}
                                    monthLabel={labels.month}
                                    childRowsOf={childRowsOf}
                                    childColumnTitle={'Recurso'}
                                />
                            </DBlock>

                            <DBlock>
                                <DBlockHeader
                                    subtitle={'Posibles faltantes'}
                                />
                                <ExpensesSummaryMissingPanel
                                    accountRows={accountRows}
                                    categoryRows={categoryRows}
                                    recurringAccountIds={recurringAccountIds}
                                    year={selected.year}
                                    month={selected.month}
                                    monthLabel={labels.month}
                                    previousMonthLabel={labels.previousMonth}
                                    includeCanceled={includeCanceled}
                                    onGenerateRecurring={() =>
                                        setGenerateOpen(true)
                                    }
                                />
                            </DBlock>

                            <DBlock>
                                <DBlockHeader
                                    subtitle={'Tendencia de 12 meses'}
                                />
                                <Typography
                                    variant={'caption'}
                                    sx={{
                                        color: 'text.secondary',
                                        display: 'block',
                                        mb: 1,
                                    }}
                                >
                                    Gasto mensual por categoría, terminando en
                                    el mes seleccionado (resaltado).
                                </Typography>
                                <ExpensesSummaryTrendChart
                                    points={categoryPoints}
                                    year={selected.year}
                                    month={selected.month}
                                    loading={loading}
                                />
                            </DBlock>
                        </>
                    )}
                </Box>
            </DPageBody>
            {/* Keyed on the selected month: the dialog seeds its target month
                from these props in useState, so remounting is what makes it
                open on the month the panel flagged rather than on today's. */}
            {selected ? (
                <SGenerateRecurringExpensesDialog
                    key={`${selected.year}-${selected.month}`}
                    open={generateOpen}
                    setOpen={setGenerateOpen}
                    initialYear={selected.year}
                    initialMonth={selected.month}
                />
            ) : null}
        </DPage>
    );
}
